import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Users } from "lucide-react";

import ExportUserToCsvButton from "../components/UserManager/User/exportToCSV.js";
import AddUserModal from "../components/UserManager/User/AddUserModal.js";
import UpdateUserModal from "../components/UserManager/User/UpdateUserModal.js";
import DeleteUserButton from "../components/UserManager/User/DeleteUserButton.js";
import UsermanMenu from "../components/UsermanMenu";

export default function UsermanUsers() {
  const token = localStorage.getItem("token");
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  /** =======================
   * STATE
   ======================= */
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const limit = 5000;

  const [openAdd, setOpenAdd] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [totalBackend, setTotalBackend] = useState(0);

  /** =======================
   * FETCH DATA (ONCE)
   ======================= */
  const fetchData = async () => {
    if (!usermanId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `https://api.wifikamu.my.id/userman-user-get/${usermanId}?page=${page}&limit=${limit}`,
        {
          method: "GET",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Request gagal");
      }

      const json = await res.json();
      setAllData(json.data || []);
      setTotalBackend(json.meta?.total || json.data.length);
      setPage(1);
    } catch (err) {
      setError(err.message || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usermanId]);

  /** =======================
   * VIEW-LEVEL SEARCH
   ======================= */
  const filteredData = useMemo(() => {
    if (!searchInput) return allData;

    const keyword = searchInput.toLowerCase();

    return allData.filter((row) => {
      return (
        row.name?.toLowerCase().includes(keyword) ||
        row.group_name?.toLowerCase().includes(keyword) ||
        row.comment?.toLowerCase().includes(keyword)
      );
    });
  }, [searchInput, allData]);

  /** =======================
   * CLIENT PAGINATION
   ======================= */
  const totalPages = Math.max(1, Math.ceil(filteredData.length / limit));

  const paginatedData = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredData.slice(start, start + limit);
  }, [filteredData, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  /** =======================
   * RENDER
   ======================= */
  return (
    <div className="p-6 dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-200">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users />
          {deviceName} /
          <UsermanMenu userman_id={usermanId} device_name={deviceName} /> / User
        </h1>

        <ExportUserToCsvButton
          data={filteredData}
          disabled={loading}
          filename={`userman-users-${usermanId}.csv`}
        />
      </div>

      {/* ACTION */}
      <div className="mb-4 flex gap-2">
        <AddUserModal
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          usermanId={usermanId}
          onSuccess={fetchData}
        />

        <button
          onClick={() => setOpenAdd(true)}
          className="px-4 py-2 bg-emerald-600 text-white rounded"
        >
          Add User
        </button>
      </div>

      {/* SEARCH */}
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Search name, group, atau comment"
          value={searchInput}
          onChange={(e) => {
            setPage(1);
            setSearchInput(e.target.value);
          }}
          className="w-full max-w-sm px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />
        <div className="flex gap-4">
          <span>
            Total MikroTik: <b>{totalBackend}</b>
          </span>
          <span>
            Filtered: <b>{filteredData.length}</b>
          </span>
          <span>
            Showing: <b>{paginatedData.length}</b>
          </span>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded border dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Group</th>
              <th className="px-4 py-2 text-center">Disabled</th>
              <th className="px-4 py-2 text-left">Comment</th>
              <th className="px-4 py-2 text-center">Edit</th>
              <th className="px-4 py-2 text-center">Delete</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center">
                  Tidak ada data
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => (
                <tr key={row.id} className="border-t dark:border-slate-700">
                  <td className="px-4 py-2">{row.name}</td>
                  <td className="px-4 py-2">{row.group_name}</td>
                  <td className="px-4 py-2 text-center">
                    {row.disabled ? (
                      <span className="text-red-500">Yes</span>
                    ) : (
                      <span className="text-green-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-2">{row.comment}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => {
                        setSelectedUser(row);
                        setOpenUpdate(true);
                      }}
                      className="px-3 py-1 text-sm rounded bg-blue-600 text-white m-1"
                    >
                      Edit
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <DeleteUserButton
                      token={token}
                      usermanId={usermanId}
                      username={row.name}
                      onSuccess={fetchData}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <UpdateUserModal
          open={openUpdate}
          onClose={() => setOpenUpdate(false)}
          token={token}
          usermanId={usermanId}
          user={selectedUser}
          onSuccess={fetchData}
        />
      </div>

      {/* PAGINATION */}
      <div className="mt-4 flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Prev
        </button>
        <span>
          Page {page} / {totalPages}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
